import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Badge,
  Avatar,
  Tag,
  Tooltip,
  Button,
  Modal,
  message,
  Empty
} from 'antd';
import { useTranslation } from 'react-i18next';
import {
  UserOutlined,
  ClockCircleOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { PlanogramTask, TaskSearchParams, TaskStatistics } from './task';
import { TaskService } from '../../services/taskService';
import TaskDetails from './TaskDetails';
import dayjs from 'dayjs';

interface TaskKanbanProps {
  searchParams: TaskSearchParams;
}

const TaskKanban: React.FC<TaskKanbanProps> = ({ searchParams }) => {
  const { t } = useTranslation();
  const [, setLoading] = useState(false);
  const [tasks, setTasks] = useState<PlanogramTask[]>([]);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [currentTask, setCurrentTask] = useState<PlanogramTask | null>(null);
  const [statistics, setStatistics] = useState<TaskStatistics | null>(null);

  // 状态列配置 - 使用英文状态键值
  const statusColumns = [
    { key: 'Not Started', title: t('taskManagement.kanban.statusColumns.notStarted'), color: '#faad14' },
    { key: 'In Progress', title: t('taskManagement.kanban.statusColumns.inProgress'), color: '#1890ff' },
    { key: 'Paused', title: t('taskManagement.kanban.statusColumns.paused'), color: '#faad14' },
  ];

  useEffect(() => {
    loadTasks();
    loadStatistics();
  }, [searchParams]);

  const loadTasks = async () => {
    setLoading(true);
    try {
      // 获取所有数据，不分页
      const allParams = { ...searchParams, pageSize: 1000 };
      const response = await TaskService.getTaskList(allParams);
      if (response.success && response.data) {
        setTasks(response.data.tableData);
      } else {
        message.error(response.errorMessage || t('taskManagement.list.messages.fetchFailed'));
      }
    } catch (error) {
      message.error(t('taskManagement.list.messages.networkError'));
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      // const response = await TaskService.getTaskStatistics();
      // if (response.success) {
      //   setStatistics(response.data);
      // }
    } catch (error) {
      console.error('Failed to load statistics:', error);
    }
  };

  // 根据状态分组任务
  const getTasksByStatus = (status: string) => {
    return tasks.filter(task => task.status === status);
  };

  // 获取状态对应的统计数量
  const getStatusCount = (status: string) => {
    if (!statistics) return 0;
    const countMap: Record<string, number> = {
      'Not Started': statistics.notStartedTasks,
      'In Progress': statistics.inProgressTasks,
      'Paused': statistics.pausedTasks,
    };
    return countMap[status] || 0;
  };

  // 获取优先级颜色 - 使用英文优先级
  const getPriorityColor = (priority: string) => {
    const colorMap: Record<string, string> = {
      'High': '#ff4d4f',
      'Medium': '#faad14',
      'Low': '#52c41a'
    };
    return colorMap[priority] || '#d9d9d9';
  };

  // 获取优先级显示文本
  const getPriorityText = (priority: string) => {
    const textMap: Record<string, string> = {
      'High': t('taskManagement.priority.high'),
      'Medium': t('taskManagement.priority.medium'),
      'Low': t('taskManagement.priority.low')
    };
    return textMap[priority] || priority;
  };

  // 查看任务详情
  const handleViewDetails = (task: PlanogramTask) => {
    setCurrentTask(task);
    setDetailsVisible(true);
  };

  const formatDateTime = (timestamp: number): string => {
    if (!timestamp) {
      return '';
    }
    return dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss');
  };

  // 渲染任务卡片
  const renderTaskCard = (task: PlanogramTask) => (
    <Card
      key={task.taskCode}
      size="small"
      style={{ 
        marginBottom: 12, 
        cursor: 'pointer',
        borderLeft: `4px solid ${getPriorityColor(task.priority)}`,
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        transition: 'all 0.3s ease'
      }}
      hoverable
      onClick={() => handleViewDetails(task)}
    >
      <div style={{ marginBottom: 12 }}>
        <div style={{ 
          fontSize: '14px', 
          fontWeight: 500,
          color: '#262626',
          marginBottom: 6
        }}>
          {task.taskCode}
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <Tag color="blue" style={{ marginBottom: 4 }}>{task.shop}</Tag>
        <Tag style={{ marginBottom: 4 }}>{task.planType}</Tag>
        <Tag color={getPriorityColor(task.priority)} style={{ marginBottom: 4 }}>
          {getPriorityText(task.priority)}
        </Tag>
      </div>

      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        fontSize: '12px',
        color: '#666'
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <ClockCircleOutlined style={{ marginRight: 4 }} />
          {formatDateTime(task.planEndDate) }
        </div>

      </div>

      <div style={{ 
        position: 'absolute',
        top: 8,
        right: 8,
        display: 'flex',
        gap: 4
      }}>
        <Tooltip title={t('taskManagement.kanban.actions.viewDetails')}>
          <Button
            type="text"
            size="small"
            icon={<EyeOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleViewDetails(task);
            }}
          />
        </Tooltip>

      </div>
    </Card>
  );

  // 渲染状态列
  const renderStatusColumn = (statusConfig: typeof statusColumns[0]) => {
    const statusTasks = getTasksByStatus(statusConfig.key);
    const count = getStatusCount(statusConfig.key);

    return (
      <Col span={8} key={statusConfig.key}>
        <Card
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 500 }}>{statusConfig.title}</span>
              <Badge 
                count={count} 
                style={{ backgroundColor: statusConfig.color }}
                overflowCount={999}
              />
            </div>
          }
          size="small"
          style={{ 
            height: '75vh',
            overflow: 'hidden',
            marginBottom: 16
          }}
          bodyStyle={{ 
            padding: '12px',
            height: 'calc(75vh - 57px)',
            overflowY: 'auto'
          }}
        >
          {statusTasks.length > 0 ? (
            statusTasks.map(renderTaskCard)
          ) : (
            <Empty 
              image={Empty.PRESENTED_IMAGE_SIMPLE} 
              description={t('taskManagement.kanban.empty.noTasks')}
              style={{ marginTop: '30%' }}
            />
          )}
        </Card>
      </Col>
    );
  };

  return (
    <div>
      <Row gutter={8}>
        {statusColumns.map(renderStatusColumn)}
      </Row>

      {/* 任务详情弹窗 */}
      <Modal
        title={t('taskManagement.details.title')}
        open={detailsVisible}
        onCancel={() => setDetailsVisible(false)}
        width={1200}
        footer={null}
      >
        {currentTask && (
          <TaskDetails task={currentTask} />
        )}
      </Modal>
    </div>
  );
};

export default TaskKanban;